// The outbox: the only way screens write to the server.
//
//   await outbox.enqueue('job.complete', { jobId, ... })
//
// The write is saved on the device first, so it survives no signal, the app
// being closed or the phone restarting, then sent when there is a
// connection. Each entry has an id made on the device; the server applies an
// id once, so sending again after a dropped connection is always safe.
//
// Statuses: pending (waiting/retrying) -> removed once applied, or
//           rejected (server will never accept it; shown to the user).

const BATCH = 50;
const MAX_BACKOFF_MS = 5 * 60_000;

export function createOutbox({ db, send, now = () => new Date() }) {
  const listeners = new Set();
  let flushing = null;
  let failures = 0;
  let retryTimer = null;

  const notify = () => listeners.forEach((fn) => fn());

  async function enqueue(type, payload) {
    const entry = {
      id: crypto.randomUUID(),
      type,
      payload,
      createdAt: now().toISOString(),
      status: 'pending',
      attempts: 0,
    };
    await db.outbox.add(entry);
    notify();
    flush(); // fire and forget; offline just leaves it queued
    return entry.id;
  }

  // Sends everything pending, oldest first, in batches. Safe to call often:
  // concurrent calls share one run.
  function flush() {
    if (!flushing) flushing = run().finally(() => { flushing = null; });
    return flushing;
  }

  async function run() {
    for (;;) {
      const batch = await db.outbox.where('status').equals('pending').sortBy('createdAt');
      if (batch.length === 0) {
        failures = 0;
        return { sent: true };
      }
      const chunk = batch.slice(0, BATCH);
      let results;
      try {
        results = await send(chunk.map(({ id, type, payload, createdAt }) => ({ id, type, payload, createdAt })));
      } catch (e) {
        // No signal, server down, or token needs a sign-in: keep everything, try later.
        scheduleRetry();
        return { sent: false, reason: e.code || 'network' };
      }
      if (!Array.isArray(results) || results.length === 0) {
        // A reply that settles nothing would loop forever; treat it as a failure.
        scheduleRetry();
        return { sent: false, reason: 'bad_reply' };
      }
      let retryable = false;
      await db.transaction('rw', db.outbox, async () => {
        for (const r of results) {
          if (r.status === 'applied' || r.status === 'duplicate') {
            await db.outbox.delete(r.id);
          } else if (r.status === 'rejected') {
            await db.outbox.update(r.id, { status: 'rejected', error: r.error, message: r.message || null });
          } else {
            retryable = true;
            await db.outbox.where('id').equals(r.id).modify((e) => { e.attempts += 1; });
          }
        }
      });
      notify();
      if (retryable) {
        // Temporary server-side failure: back off rather than hammer it.
        scheduleRetry();
        return { sent: false, reason: 'server_error' };
      }
    }
  }

  function scheduleRetry() {
    failures += 1;
    const delay = Math.min(MAX_BACKOFF_MS, 5_000 * 2 ** (failures - 1));
    clearTimeout(retryTimer);
    retryTimer = setTimeout(flush, delay);
  }

  async function counts() {
    const [pending, rejected] = await Promise.all([
      db.outbox.where('status').equals('pending').count(),
      db.outbox.where('status').equals('rejected').count(),
    ]);
    return { pending, rejected };
  }

  // A rejected item is only removed when the user chooses to discard it.
  async function discard(id) {
    await db.outbox.delete(id);
    notify();
  }

  function subscribe(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  }

  function stop() {
    clearTimeout(retryTimer);
  }

  return { enqueue, flush, counts, discard, subscribe, stop };
}
